
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import ButtonBarInput from "@/components/Reusable/CustomInputs/ButtonBarInput/ButtonBarInput.vue";
import ButtonBar from "@/components/Reusable/CustomInputs/ButtonBarInput/ButtonBar.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import AlertBox from "@/components/Reusable/CustomInputs/AlertBox.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import { AlertBoxOptions, ApiKeyOptions } from "@/types/controls";
import { configStore } from "@/store/store-accessor";
import {
  getAllApiKeys,
  validateApiKey,
  ValidatedApiKey
} from "@/lib/api-keys/ajax";
import lodash, { debounce } from "lodash";

type ApiKey = {
  type: "default" | "custom";
  apiKey: string | null;
  apiUrl?: string | null;
};

@Component({
  components: { AlertBox, TextInput, ButtonBar, ButtonBarInput, NiceButton }
})
export default class ApiKeyInput extends Vue {
  @Prop()
  value!: ApiKey | null;

  @Prop()
  options?: ApiKeyOptions;

  internalValue: ApiKey = this.value || {
    type: "default",
    apiKey: null
  };

  defaultApiKey: string | null = null;
  defaultApiUrl: string | null = null;
  customApiKeyValidated: ValidatedApiKey | null = null;
  defaultApiKeyValidated: ValidatedApiKey | null = null;
  loadingCustomApiKeyValidation = false;
  loadingDefaultApiKey = false;

  async mounted() {
    this.loadingDefaultApiKey = true;
    await this.getDefaultKeyAndValidateIt();
    await this.validateCustomApiKey();
    this.emit(this.internalValue);
    this.loadingDefaultApiKey = false;
  }

  reloadKey() {
    this.getDefaultKeyAndValidateIt();
  }

  @Watch("value")
  updateInternalValue() {
    this.internalValue = this.value || {
      type: "default",
      apiKey: null
    };

    this.getDefaultKeyAndValidateIt();
    this.validateCustomApiKey();
  }

  throttledValidateCustomApiKey = debounce(this.validateCustomApiKey, 500);

  async updateCustomKey(newKey: ApiKey["apiKey"]) {
    this.internalValue.apiKey = newKey;
    this.emit(this.internalValue);

    this.throttledValidateCustomApiKey();
  }

  async updateCustomUrl(newUrl: ApiKey["apiUrl"]) {
    this.internalValue.apiUrl = newUrl;
    this.emit(this.internalValue);

    this.throttledValidateCustomApiKey();
  }

  updateType(newType: ApiKey["type"]) {
    this.internalValue.type = newType;

    this.defaultApiKeyValidated = null;

    this.emit(this.internalValue);

    if (newType === "default") {
      this.getDefaultKeyAndValidateIt();
    }
  }

  emit(newValue: ApiKey) {
    if (
      lodash.isEqual(newValue, {
        type: "default",
        apiKey: null
      })
    ) {
      // unset the input since we're matching what the default is. In other words, the user made no change
      this.$emit("input", null);
      return;
    }

    this.$emit("input", newValue);
  }

  validationInProgress = false;

  async getDefaultKeyAndValidateIt() {
    this.validationInProgress = true;

    const allApiKeys = await getAllApiKeys();

    this.defaultApiKey = this.options?.apiKeyName
      ? allApiKeys
        ? allApiKeys[this.options.apiKeyName]
        : null
      : null;

    this.defaultApiUrl = this.options?.urlKeyName
      ? allApiKeys
        ? allApiKeys[this.options.urlKeyName]
        : null
      : null;

    await this.validateDefaultApiKey();

    this.validationInProgress = false;
  }

  async validateDefaultApiKey() {
    if (
      this.internalValue.type !== "default" ||
      !this.options?.apiKeyName ||
      !this.defaultApiKey
    ) {
      this.defaultApiKeyValidated = null;

      return;
    }

    this.defaultApiKeyValidated = await validateApiKey({
      apiKeyName: this.options.apiKeyName,
      apiKey: this.defaultApiKey,
      apiUrl: this.defaultApiUrl
    });
  }

  async validateCustomApiKey() {
    if (
      this.internalValue.type !== "custom" ||
      !this.internalValue.apiKey ||
      !this.options?.apiKeyName
    ) {
      this.customApiKeyValidated = null;

      return;
    }

    this.loadingCustomApiKeyValidation = true;

    this.customApiKeyValidated = await validateApiKey({
      apiKeyName: this.options.apiKeyName,
      apiKey: this.internalValue.apiKey,
      apiUrl: this.internalValue.apiUrl
    });

    this.loadingCustomApiKeyValidation = false;
  }

  get alertBoxDefaultApiErrorOptions() {
    const settingsPageLink = `<a href='${configStore.neededData?.breakdanceSettingsUrl}&tab=api_keys' target="_blank">WP Admin</a>`;

    if (this.defaultApiKeyValidated?.type === "error") {
      return {
        style: "error",
        content: `${this.defaultApiKeyValidated?.message}. Set it in the ${settingsPageLink}.`
      };
    }

    if (
      !this.options?.apiKeyName ||
      this.internalValue.type !== "default" ||
      this.defaultApiKey ||
      this.defaultApiKey === null
    ) {
      return false;
    }

    return {
      style: "error",
      content: `${this.apiKeyLabel} not present. Set it in the ${settingsPageLink}.`
    };
  }

  get apiKeyLabel() {
    return this.options?.apiKeyLabel || "API Key";
  }

  get urlKeyLabel() {
    return this.options?.urlKeyLabel || "URL";
  }

  get alertBoxInvalidCustomApiKeyOptions(): AlertBoxOptions | false {
    if (this.internalValue.type === "custom") {
      if (!this.internalValue.apiKey) {
        return {
          style: "warning",
          content: "Please enter an API key"
        };
      }

      if (
        this.customApiKeyValidated?.type === "error" &&
        this.customApiKeyValidated?.message
      ) {
        return {
          style: "error",
          content: this.customApiKeyValidated.message
        };
      }
    }

    return false;
  }
}
